/**
 * Minimal QR Code encoder for provenance tags.
 *
 * Generates a QR Code bit matrix for short URLs (up to ~100 chars)
 * using version 3 (29x29), error correction level M.
 * No external dependencies -- pure TypeScript canvas drawing.
 *
 * This is intentionally minimal: it only handles alphanumeric mode,
 * which is sufficient for Thread asset URLs (uppercase + digits + symbols).
 * For URLs that require byte mode, it falls back to a simple text label
 * drawn on canvas instead of a QR code.
 */

// ---------------------------------------------------------------------------
// Constants for QR Version 3-M (29x29, EC level M)
// ---------------------------------------------------------------------------

const QR_SIZE = 29; // modules per side for version 3
const DATA_CODEWORDS = 44; // Version 3-M data codewords
const EC_CODEWORDS = 26; // Version 3-M error-correction codewords
const MAX_ALPHANUMERIC_CHARS = 61; // Version 3-M alphanumeric capacity

// Generator polynomial coefficients for 26 EC codewords (version 3-M)
const GEN_POLY = [
  1, 246, 51, 183, 4, 136, 98, 199, 152, 77, 56, 206, 24, 145,
  40, 209, 117, 233, 42, 135, 68, 70, 144, 146, 77, 43, 94,
];

// Alphanumeric character map
const ALPHA_MAP: Record<string, number> = {};
"0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:".split("").forEach((c, i) => {
  ALPHA_MAP[c] = i;
});

// ---------------------------------------------------------------------------
// GF(256) arithmetic for Reed-Solomon
// ---------------------------------------------------------------------------

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(function initGaloisField() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x = x << 1;
    if (x >= 256) x ^= 0x11d; // primitive polynomial for GF(256)
  }
  for (let i = 255; i < 512; i++) {
    GF_EXP[i] = GF_EXP[i - 255]!;
  }
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[(GF_LOG[a]! + GF_LOG[b]!) % 255]!;
}

function rsEncode(data: number[], ecCount: number): number[] {
  const msg = [...data, ...new Array(ecCount).fill(0)];
  for (let i = 0; i < data.length; i++) {
    const coeff = msg[i]!;
    if (coeff !== 0) {
      for (let j = 0; j < GEN_POLY.length; j++) {
        msg[i + j] = msg[i + j]! ^ gfMul(coeff, GEN_POLY[j]!);
      }
    }
  }
  return msg.slice(data.length);
}

// ---------------------------------------------------------------------------
// Bit stream
// ---------------------------------------------------------------------------

class BitStream {
  private bits: number[] = [];

  push(value: number, length: number) {
    for (let i = length - 1; i >= 0; i--) {
      this.bits.push((value >> i) & 1);
    }
  }

  toBytes(): number[] {
    // Pad to fill data capacity
    while (this.bits.length % 8 !== 0) this.bits.push(0);
    const bytes: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j++) byte = (byte << 1) | (this.bits[i + j] ?? 0);
      bytes.push(byte);
    }
    return bytes;
  }

  get length() {
    return this.bits.length;
  }
}

// ---------------------------------------------------------------------------
// QR matrix construction
// ---------------------------------------------------------------------------

function createMatrix(): number[][] {
  return Array.from({ length: QR_SIZE }, () => new Array(QR_SIZE).fill(-1));
}

function placeFinderPattern(matrix: number[][], row: number, col: number) {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || rr >= QR_SIZE || cc < 0 || cc >= QR_SIZE) continue;
      const isBlack =
        (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
        (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      matrix[rr]![cc] = isBlack ? 1 : 0;
    }
  }
}

function placeAlignmentPattern(matrix: number[][], row: number, col: number) {
  for (let r = -2; r <= 2; r++) {
    for (let c = -2; c <= 2; c++) {
      const isBlack =
        Math.abs(r) === 2 || Math.abs(c) === 2 || (r === 0 && c === 0);
      matrix[row + r]![col + c] = isBlack ? 1 : 0;
    }
  }
}

function placeTimingPatterns(matrix: number[][]) {
  for (let i = 8; i < QR_SIZE - 8; i++) {
    const val = i % 2 === 0 ? 1 : 0;
    if (matrix[6]![i] === -1) matrix[6]![i] = val;
    if (matrix[i]![6] === -1) matrix[i]![6] = val;
  }
}

function placeFormatInfo(matrix: number[][], mask: number) {
  // Format info for EC level M (bits 00) with given mask pattern
  // EC level M = 0b00, so format data = (0b00 << 3) | mask
  const formatData = (0b00 << 3) | mask;
  // BCH(15,5) encoding with generator polynomial 0b10100110111
  const G = 0b10100110111;
  let rem = formatData << 10;
  for (let i = 4; i >= 0; i--) {
    if (rem & (1 << (i + 10))) {
      rem ^= G << i;
    }
  }
  const bits = ((formatData << 10) | rem) ^ 0b101010000010010;

  // Place format bits around finder patterns
  const positions1 = [
    [0, 8], [1, 8], [2, 8], [3, 8], [4, 8], [5, 8], [7, 8], [8, 8],
    [8, 7], [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0],
  ];
  const positions2 = [
    [8, QR_SIZE - 1], [8, QR_SIZE - 2], [8, QR_SIZE - 3], [8, QR_SIZE - 4],
    [8, QR_SIZE - 5], [8, QR_SIZE - 6], [8, QR_SIZE - 7], [8, QR_SIZE - 8],
    [QR_SIZE - 7, 8], [QR_SIZE - 6, 8], [QR_SIZE - 5, 8], [QR_SIZE - 4, 8],
    [QR_SIZE - 3, 8], [QR_SIZE - 2, 8], [QR_SIZE - 1, 8],
  ];

  // Dark module
  matrix[QR_SIZE - 8]![8] = 1;

  for (let i = 0; i < 15; i++) {
    const bit = (bits >> (14 - i)) & 1;
    const p1 = positions1[i]!;
    matrix[p1[0]!]![p1[1]!] = bit;
    const p2 = positions2[i]!;
    matrix[p2[0]!]![p2[1]!] = bit;
  }
}

function placeVersionInfo(_matrix: number[][]) {
  // Version 3 does not require version info (only version >= 7)
}

function placeDataBits(matrix: number[][], dataBits: number[]) {
  let bitIdx = 0;
  let upward = true;
  for (let col = QR_SIZE - 1; col >= 0; col -= 2) {
    if (col === 6) col = 5; // Skip timing pattern column
    const rows = upward
      ? Array.from({ length: QR_SIZE }, (_, i) => QR_SIZE - 1 - i)
      : Array.from({ length: QR_SIZE }, (_, i) => i);
    for (const row of rows) {
      for (const c of [col, col - 1]) {
        if (c < 0 || c >= QR_SIZE) continue;
        if (matrix[row]![c] !== -1) continue;
        matrix[row]![c] = bitIdx < dataBits.length ? dataBits[bitIdx]! : 0;
        bitIdx++;
      }
    }
    upward = !upward;
  }
}

function applyMask(matrix: number[][], reserved: number[][], mask: number): number[][] {
  const result = matrix.map((row) => [...row]);
  for (let r = 0; r < QR_SIZE; r++) {
    for (let c = 0; c < QR_SIZE; c++) {
      if (reserved[r]![c] !== -1) continue;
      let shouldFlip = false;
      switch (mask) {
        case 0: shouldFlip = (r + c) % 2 === 0; break;
        case 1: shouldFlip = r % 2 === 0; break;
        case 2: shouldFlip = c % 3 === 0; break;
        case 3: shouldFlip = (r + c) % 3 === 0; break;
        case 4: shouldFlip = (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0; break;
        case 5: shouldFlip = (r * c) % 2 + (r * c) % 3 === 0; break;
        case 6: shouldFlip = ((r * c) % 2 + (r * c) % 3) % 2 === 0; break;
        case 7: shouldFlip = ((r + c) % 2 + (r * c) % 3) % 2 === 0; break;
      }
      if (shouldFlip) result[r]![c] = result[r]![c]! ^ 1;
    }
  }
  return result;
}

function penaltyScore(matrix: number[][]): number {
  let score = 0;
  // Rule 1: consecutive same-color modules in rows/cols
  for (let r = 0; r < QR_SIZE; r++) {
    let count = 1;
    for (let c = 1; c < QR_SIZE; c++) {
      if (matrix[r]![c] === matrix[r]![c - 1]) {
        count++;
        if (count === 5) score += 3;
        else if (count > 5) score += 1;
      } else {
        count = 1;
      }
    }
  }
  for (let c = 0; c < QR_SIZE; c++) {
    let count = 1;
    for (let r = 1; r < QR_SIZE; r++) {
      if (matrix[r]![c] === matrix[r - 1]![c]) {
        count++;
        if (count === 5) score += 3;
        else if (count > 5) score += 1;
      } else {
        count = 1;
      }
    }
  }
  return score;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check if a string can be encoded in alphanumeric mode.
 */
export function canEncodeAlphanumeric(text: string): boolean {
  return text.split("").every((c) => c in ALPHA_MAP);
}

/**
 * Encode a short string into a QR code bit matrix (version 3-M, 29x29).
 * Returns a 2D boolean array where true = dark module.
 *
 * The input should be uppercase alphanumeric (Thread URLs).
 * Returns null if the input is too long or cannot be encoded.
 */
export function encodeQR(text: string): boolean[][] | null {
  const upper = text.toUpperCase();
  if (!canEncodeAlphanumeric(upper)) return null;
  if (upper.length > MAX_ALPHANUMERIC_CHARS) return null;

  // Encode data
  const stream = new BitStream();
  stream.push(0b0010, 4); // Alphanumeric mode indicator
  stream.push(upper.length, 9); // Character count (9 bits for version 1-9)

  for (let i = 0; i < upper.length; i += 2) {
    if (i + 1 < upper.length) {
      const pair = ALPHA_MAP[upper[i]!]! * 45 + ALPHA_MAP[upper[i + 1]!]!;
      stream.push(pair, 11);
    } else {
      stream.push(ALPHA_MAP[upper[i]!]!, 6);
    }
  }

  // Terminator
  stream.push(0, Math.min(4, DATA_CODEWORDS * 8 - stream.length));

  const dataBytes = stream.toBytes();

  // Pad to fill V3-M data capacity (44 data codewords).
  while (dataBytes.length < DATA_CODEWORDS) {
    dataBytes.push(dataBytes.length % 2 === 0 ? 0xec : 0x11);
  }
  dataBytes.length = DATA_CODEWORDS;

  // Reed-Solomon error correction
  const ecCodewords = rsEncode(dataBytes, EC_CODEWORDS);
  const allCodewords = [...dataBytes, ...ecCodewords];

  // Convert to bit array
  const dataBits: number[] = [];
  for (const byte of allCodewords) {
    for (let bit = 7; bit >= 0; bit--) {
      dataBits.push((byte >> bit) & 1);
    }
  }

  // Build the matrix
  const reserved = createMatrix();
  placeFinderPattern(reserved, 0, 0);
  placeFinderPattern(reserved, 0, QR_SIZE - 7);
  placeFinderPattern(reserved, QR_SIZE - 7, 0);
  placeAlignmentPattern(reserved, 22, 22); // Version 3 alignment at (22,22)
  placeTimingPatterns(reserved);
  reserved[QR_SIZE - 8]![8] = 1; // Dark module
  // Reserve format info areas
  for (let i = 0; i < 8; i++) {
    if (reserved[8]![i] === -1) reserved[8]![i] = 0;
    if (reserved[i]![8] === -1) reserved[i]![8] = 0;
    if (reserved[8]![QR_SIZE - 1 - i] === -1) reserved[8]![QR_SIZE - 1 - i] = 0;
    if (reserved[QR_SIZE - 1 - i]![8] === -1) reserved[QR_SIZE - 1 - i]![8] = 0;
  }
  if (reserved[8]![8] === -1) reserved[8]![8] = 0;

  // Place data
  const matrix = reserved.map((r) => [...r]);
  placeDataBits(matrix, dataBits);

  // Try all mask patterns, pick best
  let bestMatrix = matrix;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const masked = applyMask(matrix, reserved, mask);
    // Place format info for this mask
    const withFormat = masked.map((r) => [...r]);
    placeFormatInfo(withFormat, mask);
    placeVersionInfo(withFormat);
    const penalty = penaltyScore(withFormat);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMatrix = withFormat;
    }
  }

  return bestMatrix.map((row) => row.map((v) => v === 1));
}

/**
 * Draw a QR code onto a canvas 2D context at the specified position and size.
 * If the URL cannot be QR-encoded, draws a text fallback instead.
 */
export function drawQROnCanvas(
  ctx: CanvasRenderingContext2D,
  url: string,
  x: number,
  y: number,
  size: number,
): void {
  const matrix = encodeQR(url);
  if (!matrix) {
    // Fallback: draw a bordered rectangle with the URL text
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(x, y, size, size);
    ctx.strokeStyle = "#333333";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, size, size);
    ctx.fillStyle = "#333333";
    ctx.font = `${Math.max(8, size / 8)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("QR", x + size / 2, y + size / 2);
    return;
  }

  const moduleCount = matrix.length;
  const moduleSize = size / (moduleCount + 2); // +2 for quiet zone
  const offset = moduleSize; // Quiet zone

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x, y, size, size);

  // Modules
  ctx.fillStyle = "#000000";
  for (let r = 0; r < moduleCount; r++) {
    for (let c = 0; c < moduleCount; c++) {
      if (matrix[r]![c]) {
        ctx.fillRect(
          x + offset + c * moduleSize,
          y + offset + r * moduleSize,
          moduleSize + 0.5, // Slight overlap to avoid gaps
          moduleSize + 0.5,
        );
      }
    }
  }
}

/** Get the QR module size (29 for version 3). */
export const QR_MODULE_COUNT = QR_SIZE;
